"use client";

import { useState, useEffect, useCallback } from "react";
import { WeatherData, TempUnit, Language } from "@/types/weather";
import { getPageBackground } from "@/lib/weatherTheme";
import { t } from "@/lib/i18n";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import WeatherCard from "@/components/WeatherCard";

interface WeatherDashboardProps {
  initialWeathers: WeatherData[];
  initialFetchedAt: string;
}

export default function WeatherDashboard({
  initialWeathers,
  initialFetchedAt,
}: WeatherDashboardProps) {
  const [weathers, setWeathers] = useState<WeatherData[]>(initialWeathers);
  const [fetchedAt, setFetchedAt] = useState(initialFetchedAt);
  const [unit, setUnit] = useState<TempUnit>("celsius");
  const [lang, setLang] = useState<Language>("en");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [localTimes, setLocalTimes] = useState<Record<string, string>>({});

  // Update local times every second
  useEffect(() => {
    const updateTimes = () => {
      const times: Record<string, string> = {};
      weathers.forEach((w) => {
        times[w.city.id] = new Date().toLocaleTimeString("en-GB", {
          timeZone: w.city.timezone,
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        });
      });
      setLocalTimes(times);
    };
    updateTimes();
    const interval = setInterval(updateTimes, 1000);
    return () => clearInterval(interval);
  }, [weathers]);

  // Auto-refresh every 10 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      handleRefresh();
    }, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const res = await fetch("/api/weather", { cache: "no-store" });
      if (res.ok) {
        const data = await res.json();
        setWeathers(data.cities);
        setFetchedAt(data.fetchedAt);
      }
    } catch (e) {
      console.error("Refresh failed:", e);
    } finally {
      setIsRefreshing(false);
    }
  }, []);

  const pageBgClass = getPageBackground(
    weathers.map((w) => ({ weatherCode: w.weatherCode, isDay: w.isDay }))
  );

  const formattedTime = new Date(fetchedAt).toLocaleTimeString(
    lang === "ja" ? "ja-JP" : "en-GB",
    { hour: "2-digit", minute: "2-digit" }
  );

  return (
    <div
      className={`min-h-screen flex flex-col bg-gradient-to-br ${pageBgClass} transition-colors duration-1000`}
    >
      <Header
        unit={unit}
        lang={lang}
        onUnitToggle={() =>
          setUnit((u) => (u === "celsius" ? "fahrenheit" : "celsius"))
        }
        onLangToggle={() => setLang((l) => (l === "en" ? "ja" : "en"))}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />

      <main className="flex-1 container mx-auto px-4 py-10">
        {/* Hero */}
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
            {t(lang, "worldWeather")}
          </h1>
          <p className="text-blue-300 text-lg">{t(lang, "realtime")}</p>
          <p className="text-slate-500 text-sm mt-2">
            {t(lang, "updated")}: {formattedTime}
            {isRefreshing && (
              <span className="ml-2 text-blue-400 animate-pulse">
                {lang === "ja" ? "更新中..." : "Refreshing..."}
              </span>
            )}
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
          {weathers.map((weather) => (
            <WeatherCard
              key={weather.city.id}
              weather={weather}
              unit={unit}
              lang={lang}
            />
          ))}
        </div>
      </main>

      <Footer lang={lang} />
    </div>
  );
}
